/**
 * ProfileTypePicker — pierwszy ekran po rejestracji (HOME FIRST LOGIN — segment).
 *
 * Ekran SPRZEDAJE wartość: dla kogo jest APISAI, co user dostanie, kolejne kroki.
 * Layout wg mockupu Ani (2026-05-31): lewa kolumna (segmenty Hodowca/Firma + zdjęcia
 * + pasek zaufania), prawa kolumna (APIS z maskotką + Usługi Premium + pomoc).
 * Render WEWNĄTRZ StudioShell (workspace + sidebar). DNA: Navy / Orange, zero fioletu.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  PawPrint, Briefcase, Check, ArrowRight, Loader2, Heart, Sparkles,
  Globe2, MapPin, Megaphone, ChevronRight, ShieldCheck, Rocket,
  TrendingUp, Headphones, MessagesSquare, Clock, HelpCircle,
} from 'lucide-react';
import { API } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import StudioShell from '@/components/studio/StudioShell';

const MASCOT = '/assets/apis/mascot.png';

const SEGMENTS = [
  {
    id: 'hodowla',
    icon: PawPrint,
    title: 'Hodowca',
    desc: 'Twórz strony hodowli, reproduktorów i miotów. Pojawiaj się w katalogu i zbieraj zapytania.',
    benefits: ['Strona hodowli', 'Reproduktory', 'Mioty', 'Katalog hodowli', 'Zapytania od klientów', 'Kalendarz ciąży'],
    cta: 'Jestem hodowcą',
    scene: '/assets/apis/scene_hodowca.png?v=3',
    route: '/panel',
  },
  {
    id: 'biznes',
    icon: Briefcase,
    title: 'Firma / Usługi',
    desc: 'Promuj swoje usługi i zdobywaj klientów online.',
    benefits: ['Strona firmy', 'Oferta usług', 'Portfolio', 'Zapytania od klientów', 'Widoczność online'],
    cta: 'Prowadzę firmę',
    scene: '/assets/apis/scene_firma.png?v=3',
    route: '/wizard/firma-v2',
  },
];

const TRUST = [
  { icon: ShieldCheck, title: 'Bezpieczne dane', sub: 'Twoje dane są u nas w pełni bezpieczne' },
  { icon: Rocket, title: 'Szybki start', sub: 'Uruchomisz swoją stronę w kilka prostych kroków' },
  { icon: TrendingUp, title: 'Więcej zapytań', sub: 'Zwiększ widoczność i zdobądź realnych klientów' },
  { icon: Headphones, title: 'Wsparcie APIS', sub: 'Jesteśmy z Tobą na każdym etapie' },
];

const PREMIUM = [
  { key: 'strona-premium', icon: Globe2, title: 'Strona Premium', sub: 'Profesjonalna strona od APIS' },
  { key: 'grafika-hodowli', icon: PawPrint, title: 'Grafika hodowli', sub: 'Logo, banery, materiały' },
  { key: 'grafika-reproduktora', icon: PawPrint, title: 'Grafika reproduktora / miotu', sub: 'Profesjonalne prezentacje' },
  { key: 'wizytowka-google', icon: MapPin, title: 'Wizytówka Google', sub: 'Więcej klientów z lokalnych wyszukiwań' },
  { key: 'kampania-meta', icon: Megaphone, title: 'Kampania Meta', sub: 'Reklamy, które przynoszą zapytania' },
  { key: 'inne', icon: Sparkles, title: 'Inne usługi', sub: 'Dopasujemy ofertę do Ciebie' },
];

export default function ProfileTypePicker() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [submittingId, setSubmittingId] = useState(null);

  const handleSelect = async (seg) => {
    if (submittingId) return;
    setSubmittingId(seg.id);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/profile/profile-type`, { profile_type: seg.id }, { headers: { Authorization: `Bearer ${token}` } });
      try { localStorage.setItem('apisai_profile_type', seg.id); } catch (e) { /* noop */ }
      if (refreshUser) await refreshUser();
      toast.success(seg.id === 'hodowla' ? 'Profil hodowcy ustawiony!' : 'Profil firmy ustawiony!');
      setTimeout(() => navigate(seg.route), 450);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Nie udało się zapisać typu profilu');
      setSubmittingId(null);
    }
  };

  return (
    <StudioShell mode="workspace" activeKey="home" contextLabel="Start">
      <main className="flex-1 overflow-y-auto apisai-workspace-bg" data-testid="profile-type-picker">
        <div className="max-w-[1240px] mx-auto px-4 sm:px-8 lg:px-10 py-8 sm:py-10">
          <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_0.62fr] gap-6 lg:gap-9 items-start">

            {/* ============ LEFT ============ */}
            <div className="space-y-6">
              {/* Header */}
              <header className="space-y-2.5">
                <div className="inline-flex items-center gap-1.5">
                  <span className="text-[13px] font-semibold uppercase tracking-wide text-[#EA580C]">Witaj w APISAI</span>
                  <Heart className="w-4 h-4 text-[#F97316] fill-[#F97316]" />
                </div>
                <h1 className="text-3xl sm:text-4xl lg:text-[2.7rem] font-bold text-[#04202F] tracking-tight leading-[1.1]">
                  Pomożemy Ci rozwinąć<br />Twoją hodowlę lub biznes online.
                </h1>
                <p className="text-[#64748B] text-base sm:text-lg max-w-2xl leading-relaxed">
                  Wybierz obszar działalności, a przygotujemy odpowiednie narzędzia i kolejne kroki.
                </p>
              </header>

              {/* Segment cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="profile-segments">
                {SEGMENTS.map((seg) => {
                  const Icon = seg.icon;
                  const isLoading = submittingId === seg.id;
                  const primary = seg.id === 'hodowla';
                  return (
                    <div
                      key={seg.id}
                      data-testid={`segment-card-${seg.id}`}
                      className="relative rounded-3xl border p-6 pb-6 flex flex-col overflow-hidden min-h-[520px]"
                      style={primary
                        ? { background: 'linear-gradient(180deg,#FFF4EA 0%,#FFFBF7 100%)', borderColor: 'rgba(249,115,22,0.22)' }
                        : { background: 'linear-gradient(180deg,#EEF3F8 0%,#F8FAFC 100%)', borderColor: 'rgba(4,32,47,0.12)' }}
                    >
                      <div className="flex items-center gap-3.5 relative z-10">
                        <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-md flex-shrink-0"
                          style={{ background: primary ? '#F97316' : '#04202F' }}>
                          <Icon className="w-7 h-7 text-white" strokeWidth={1.7} />
                        </div>
                        <h2 className="text-2xl font-bold text-[#04202F] tracking-tight">{seg.title}</h2>
                      </div>

                      <p className="text-[#475569] text-sm mt-3.5 leading-relaxed relative z-10 max-w-[88%]">{seg.desc}</p>

                      {/* benefits + scena z maskotką */}
                      <div className="relative flex-1 mt-4">
                        <ul className="space-y-2.5 relative z-10 sm:pr-[44%]">
                          {seg.benefits.map((b) => (
                            <li key={b} className="flex items-center gap-2.5">
                              <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                                style={{ background: primary ? '#F97316' : '#04202F' }}>
                                <Check className="w-3 h-3 text-white" strokeWidth={3} />
                              </span>
                              <span className="text-[#1E293B] text-[15px] font-medium">{b}</span>
                            </li>
                          ))}
                        </ul>
                        {/* maskotka w scenerii — prawa dolna część karty */}
                        <img
                          src={seg.scene}
                          alt=""
                          aria-hidden="true"
                          className="hidden sm:block absolute right-[-18px] bottom-0 w-[56%] max-w-[250px] max-h-[290px] object-contain object-bottom pointer-events-none select-none"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={() => handleSelect(seg)}
                        disabled={!!submittingId}
                        data-testid={`segment-cta-${seg.id}`}
                        className="mt-5 w-full inline-flex items-center justify-center gap-2 h-12 rounded-xl text-white text-[15px] font-semibold transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0 relative z-10"
                        style={primary
                          ? { background: '#F97316', boxShadow: '0 14px 30px -10px rgba(249,115,22,0.5)' }
                          : { background: '#04202F', boxShadow: '0 14px 30px -12px rgba(4,32,47,0.5)' }}
                      >
                        {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                        {seg.cta}
                        {!isLoading && <ArrowRight className="w-4 h-4" />}
                      </button>

                      {/* Step meta */}
                      <div className="mt-3 flex items-center justify-center gap-1.5 text-[#64748B] text-xs relative z-10">
                        <Clock className="w-3.5 h-3.5" />
                        <span>Krok 1 z 4 · Zajmuje około 3 minuty</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Trzecia ścieżka — dla niezdecydowanych (Start Mode: „Nie wiem od czego zacząć") */}
              <button
                type="button"
                onClick={() => navigate('/pomoc')}
                data-testid="segment-cta-niewiem"
                className="group w-full flex items-center gap-3.5 rounded-2xl border border-[rgba(4,32,47,0.12)] bg-white px-5 py-4 text-left hover:border-[#F97316] transition-colors"
              >
                <div className="w-11 h-11 rounded-xl bg-[#F97316]/10 flex items-center justify-center flex-shrink-0">
                  <HelpCircle className="w-5 h-5 text-[#F97316]" strokeWidth={1.9} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[#04202F] text-base font-bold leading-tight">Nie wiem od czego zacząć</p>
                  <p className="text-[#64748B] text-sm leading-snug mt-0.5">Porozmawiaj z APIS — pomoże dobrać ścieżkę i poprowadzi Cię krok po kroku.</p>
                </div>
                <ArrowRight className="w-5 h-5 text-[#CBD5E1] group-hover:text-[#F97316] group-hover:translate-x-0.5 transition-all flex-shrink-0" />
              </button>

              {/* Trust strip */}
              <div className="rounded-2xl bg-white border border-[rgba(4,32,47,0.08)] px-5 py-5 grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="profile-trust">
                {TRUST.map((t) => {
                  const Icon = t.icon;
                  return (
                    <div key={t.title} className="flex items-start gap-3">
                      <Icon className="w-5 h-5 text-[#F97316] flex-shrink-0 mt-0.5" strokeWidth={1.9} />
                      <div className="min-w-0">
                        <p className="text-[#04202F] text-sm font-bold leading-tight">{t.title}</p>
                        <p className="text-[#64748B] text-xs mt-0.5 leading-snug">{t.sub}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ============ RIGHT ============ */}
            <aside className="space-y-5" data-testid="profile-aside">
              {/* APIS + mascot */}
              <div className="relative rounded-3xl p-5 pb-5 overflow-hidden border"
                style={{ background: 'linear-gradient(165deg,#FFF1E6 0%,#FFFCF9 70%)', borderColor: 'rgba(249,115,22,0.22)' }}>
                <div className="flex items-start gap-2.5 relative z-10">
                  <PawPrint className="w-6 h-6 text-[#F97316] flex-shrink-0" strokeWidth={1.9} />
                  <div>
                    <p className="text-[#EA580C] text-lg font-bold leading-tight">APIS</p>
                    <p className="text-[#64748B] text-xs">Twój asystent rozwoju</p>
                  </div>
                </div>
                <img src={MASCOT} alt="APIS" className="absolute right-1 top-1 w-28 sm:w-32 object-contain pointer-events-none select-none" />
                <p className="text-[#475569] text-sm leading-relaxed mt-4 relative z-10 max-w-[62%]">
                  Cześć! Pomogę Ci wybrać najlepszą ścieżkę i poprowadzę Cię krok po kroku do sukcesu.
                </p>
                <button
                  onClick={() => navigate('/pomoc')}
                  data-testid="apis-talk-btn"
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-semibold transition-colors relative z-10"
                  style={{ boxShadow: '0 12px 28px -10px rgba(249,115,22,0.5)' }}
                >
                  Porozmawiajmy <ArrowRight className="w-4 h-4" />
                </button>
              </div>

              {/* Usługi Premium */}
              <div className="rounded-3xl bg-white border border-[rgba(4,32,47,0.10)] p-5" data-testid="profile-premium">
                <p className="text-[11px] uppercase tracking-wider font-semibold text-[#94A3B8] mb-1">Usługi Premium</p>
                <p className="text-[#04202F] text-base font-bold mb-3.5">Potrzebujesz czegoś więcej?</p>
                <div className="space-y-1.5">
                  {PREMIUM.map((p) => {
                    const Icon = p.icon;
                    return (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => navigate(`/panel/uslugi-premium?usluga=${p.key}`)}
                        data-testid={`profile-premium-${p.key}`}
                        className="group w-full flex items-center gap-3 px-2.5 py-2.5 rounded-xl hover:bg-[#FBF8F3] transition-colors text-left"
                      >
                        <div className="w-9 h-9 rounded-lg bg-[#F97316]/10 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-4 h-4 text-[#F97316]" strokeWidth={1.9} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[#04202F] text-sm font-semibold leading-tight">{p.title}</p>
                          <p className="text-[#64748B] text-xs leading-snug truncate">{p.sub}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-[#CBD5E1] group-hover:text-[#F97316] group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Pomoc */}
              <div className="relative rounded-3xl bg-white border border-[rgba(4,32,47,0.10)] p-5 overflow-hidden" data-testid="profile-help">
                <p className="text-[#04202F] text-base font-bold relative z-10">Potrzebujesz pomocy?</p>
                <p className="text-[#64748B] text-sm mt-1 max-w-[72%] relative z-10">Napisz do nas lub umów rozmowę. Jesteśmy, aby Ci pomóc.</p>
                <MessagesSquare className="absolute right-3 top-4 w-12 h-12 text-[#04202F]/10" strokeWidth={1.4} />
                <button
                  onClick={() => navigate('/pomoc')}
                  data-testid="profile-help-cta"
                  className="mt-4 inline-flex items-center gap-2 h-10 px-5 rounded-xl border border-[#04202F]/15 text-[#04202F] text-sm font-semibold hover:border-[#F97316] hover:text-[#EA580C] transition-colors relative z-10"
                >
                  Skontaktuj się <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </aside>
          </div>
        </div>
      </main>
    </StudioShell>
  );
}
